<template>
  <div :key="jobId" class="job-dropdown">
    <ButtonSplit :size="size" :placement="PopperPlacements.rightStart">
      <template #default>
        <template v-if="!isJobOpen">
          <a
            v-if="isJobPage"
            :data-swaltext="
              $t('Do you want to terminate this job and terminate any ongoing job process?')
            "
            :data-swalconfirmtext="$t('Yes, mark the job as completed')"
            :data-swalcompletedtext="$t('The job closes now')"
            :data-formid="`form-job-close-${jobId}`"
            href="#"
            class="swal-delete"
          >
            {{ $t('End job') }}
          </a>
          <a v-else :href="viewJobRouteUrl({ job: jobId })">
            {{ $t('View job') }}
          </a>
        </template>
        <a v-else-if="!isJobRemoved" :href="link">
          {{ label }}
        </a>
        <a
          v-else
          :data-swaltext="$t('Do you want to resume this job?')"
          :data-swalconfirmtext="$t('Yes, resume the job')"
          :data-swalcompletedtext="$t('The job is resumed')"
          :data-formid="`form-job-restore-${jobId}`"
          class="swal-delete"
        >
          {{ $t('Recreate job') }}
        </a>
      </template>
      <template #dropdown>
        <template v-if="!isJobRemoved && isJobCreated">
          <template v-if="isJobAvailable && isJobOpen">
            <!--
              Briefs use a single one-page form instead of the full
              multi-tab edit; show one "Edit" entry instead of three tabs.
            -->
            <ButtonSplitList v-if="isBrief">
              <li>
                <a :href="briefEditUrl">{{ $t('Edit') }}</a>
              </li>
            </ButtonSplitList>
            <ButtonSplitList v-else>
              <li>
                <a :href="`${editJob}?tab=${JOB_TAB.OVERVIEW}`">{{ $t('Overview') }}</a>
              </li>
              <li>
                <a :href="`${editJob}?tab=${JOB_TAB.DETAILS}`">{{ $t('Details') }}</a>
              </li>
              <li>
                <a :href="`${editJob}?tab=${JOB_TAB.BUDGET}`">{{ $t('Budget') }}</a>
              </li>
            </ButtonSplitList>
            <ButtonSplitList>
              <li>
                <a :href="shareJobUrl">{{ $t('Sharing') }}</a>
              </li>
            </ButtonSplitList>
          </template>
          <ButtonSplitList v-if="showCustomFields && !isJobOpen">
            <li>
              <a :href="editFieldsUrl">
                {{ $t('Custom fields') }}
              </a>
            </li>
          </ButtonSplitList>
        </template>

        <ButtonSplitList>
          <!-- Bids/pause actions don't apply to briefs (no sourcing). -->
          <li v-if="!isBrief && !isJobPaused && isJobAvailable && isJobApproved">
            <a
              :data-swaltext="$t('Do you want to pause bids for this job post?')"
              :data-swalconfirmtext="$t('Yes, pause the job post')"
              :data-swalcompletedtext="$t('The job post is paused')"
              :data-formid="`form-job-pause-${jobId}`"
              href="#"
              class="swal-delete"
            >
              {{ $t('Pause bids') }}
            </a>
          </li>
          <li v-else-if="!isBrief && isJobPaused">
            <a
              :data-swaltext="$t('Do you want to resume bids for this job post?')"
              :data-swalconfirmtext="$t('Yes, resume the job post')"
              :data-swalcompletedtext="$t('The job post is resumed')"
              :data-formid="`form-job-unpause-${jobId}`"
              href="#"
              class="swal-delete"
            >
              {{ $t('Resume bids') }}
            </a>
          </li>
          <!-- Add delete, soft delete and restore method-->
          <li v-if="canBeDeleted">
            <a
              :data-swaltext="$t('Do you want to remove this job completely?')"
              :data-formid="`form-job-delete-${jobId}`"
              href="#"
              class="swal-delete"
            >
              {{ $t('Delete job') }}
            </a>
          </li>
          <li v-else-if="canBeEnded">
            <a
              :data-swaltext="
                $t('Do you want to terminate this job and terminate any ongoing job process?')
              "
              :data-swalconfirmtext="$t('Yes, mark the job as completed')"
              :data-swalcompletedtext="$t('The job closes now')"
              :data-formid="`form-job-close-${jobId}`"
              href="#"
              class="swal-delete"
            >
              {{ $t('End job') }}
            </a>
          </li>
          <template v-else-if="canBeRemoved">
            <template v-if="isJobRemoved">
              <li>
                <a
                  :data-swaltext="$t('Do you want to resume this job?')"
                  :data-swalconfirmtext="$t('Yes, resume the job')"
                  :data-swalcompletedtext="$t('The job is resumed')"
                  :data-formid="`form-job-restore-${jobId}`"
                  href="#"
                  class="swal-delete"
                >
                  {{ $t('Recreate job') }}
                </a>
              </li>
            </template>
            <template v-else>
              <li>
                <a :href="removeJobRouteUrl({ yjob: jobId })">{{ $t('Remove job') }}</a>
              </li>
            </template>
          </template>
        </ButtonSplitList>
      </template>
    </ButtonSplit>

    <BaseHtmlForm
      :id="`form-job-delete-${jobId}`"
      :action="`/job/${jobId}`"
      method="DELETE"
      hidden
      append-user-and-account
    />
    <BaseHtmlForm
      :id="`form-job-close-${jobId}`"
      :action="`/job/${jobId}/close`"
      method="PATCH"
      hidden
      append-user-and-account
    />
    <BaseHtmlForm
      :id="`form-job-unpublish-${jobId}`"
      :action="`/job/${jobId}/unpublish`"
      method="PATCH"
      hidden
      append-user-and-account
    />
    <BaseHtmlForm
      :id="`form-job-restore-${jobId}`"
      :action="`/job/${jobId}/restore`"
      method="PATCH"
      hidden
      append-user-and-account
    />
    <BaseHtmlForm
      :id="`form-job-pause-${jobId}`"
      :action="`/jobs/${jobId}/pause`"
      method="post"
      hidden
      append-user-and-account
    />
    <BaseHtmlForm
      :id="`form-job-unpause-${jobId}`"
      :action="`/jobs/${jobId}/unpause`"
      method="post"
      hidden
      append-user-and-account
    />
  </div>
</template>

<script>
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import { PopperPlacements } from '@/utils/enum'
import laravelNamedRoutes from '@/utils/laravelNamedRoutes.json'

import ButtonSplit from '@/components/ButtonSplit.vue'
import ButtonSplitList from '@/components/ButtonSplitList.vue'
import FeatureFlagged from '@/components/FeatureFlagged.vue'

import { JOB_TAB } from '@/components/Job/constants'
import BaseHtmlForm from '@/components/BaseHtmlForm.vue'

export default {
  components: {
    BaseHtmlForm,
    // eslint-disable-next-line vue/no-unused-components -- Baseline eslint ignore please fix if working on this
    FeatureFlagged,
    ButtonSplit,
    ButtonSplitList,
  },
  props: {
    jobId: {
      type: Number,
      required: true,
    },
    isJobPage: {
      type: Boolean,
      default: false,
    },
    isJobCompleted: {
      type: Boolean,
      required: true,
    },
    hasJobHires: {
      type: Boolean,
      required: true,
    },
    hasJobDialogs: {
      type: Boolean,
      required: true,
    },
    isJobAvailable: {
      type: Boolean,
      required: true,
    },
    isJobApproved: {
      type: Boolean,
      required: true,
    },
    isJobOpen: {
      type: Boolean,
      required: true,
    },
    currentJobStep: {
      type: String,
      required: true,
    },
    isJobRemoved: {
      type: Boolean,
      required: true,
    },
    isJobCreated: {
      type: Boolean,
      required: true,
    },
    isJobPaused: {
      type: Boolean,
      required: true,
    },
    // true if current user can manage fields AND either:
    // user has teammates OR custom fields were added in company settings > custom fields
    showCustomFields: {
      type: Boolean,
      required: true,
    },
    size: {
      type: String,
      default: 'xs',
    },
    buttonLabel: {
      type: String,
      required: false,
      default: null,
    },
    buttonLink: {
      type: String,
      required: false,
      default: null,
    },
    sharedWithTrustedContacts: {
      type: Boolean,
      default: false,
    },
    sharedWithRecruiters: {
      type: Boolean,
      default: false,
    },
    isBrief: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      JOB_TAB,
    }
  },
  computed: {
    PopperPlacements() {
      return PopperPlacements
    },
    label() {
      // optional customlabel
      if (this.buttonLabel) {
        return this.buttonLabel
      }

      // original
      return $t('Manage job')
    },
    link() {
      // optional custom link
      if (this.buttonLink) {
        return this.buttonLink
      }

      // original
      return this.editJobHref
    },
    editJob() {
      return laravelNamedRoute(laravelNamedRoutes.yjob.edit, { yjob: this.jobId })
    },
    briefEditUrl() {
      return laravelNamedRoute(laravelNamedRoutes.frontend.flows.hiring['brief-edit'], {
        yjob: this.jobId,
      })
    },

    steps() {
      return [
        {
          text: $t('Title and skills'),
          link: `${this.editJob}?step=1`,
        },
        {
          text: $t('Description and files'),
          link: `${this.editJob}?step=2`,
        },
        {
          text: $t('Scope and place'),
          link: `${this.editJob}?step=3`,
        },
        {
          text: $t('Time and payment'),
          link: `${this.editJob}?step=4`,
        },
      ]
    },
    editFieldsUrl() {
      return laravelNamedRoute('yjob.custom-fields', {
        yjob: this.jobId,
      })
    },
    editJobHref() {
      let base = this.editJob
      if (!this.isJobCreated) {
        base += `?step=${this.currentJobStep}`
      }
      return base
    },
    shareJobUrl() {
      return laravelNamedRoute(laravelNamedRoutes.jobs.candidates, { job: this.jobId })
    },
    canBeEnded() {
      return !this.isJobCompleted && this.hasJobHires
    },
    canBeRemoved() {
      return !this.isJobCompleted && !this.hasJobHires
    },
    canBeDeleted() {
      return !this.hasJobHires && !this.hasJobDialogs && !this.isJobRemoved && !this.isJobCompleted
    },
  },
  methods: {
    viewJobRouteUrl(params) {
      return laravelNamedRoute(laravelNamedRoutes.jobs.show, params)
    },

    removeJobRouteUrl(params) {
      return laravelNamedRoute(laravelNamedRoutes.frontend.jobs.removeconfirm, params)
    },
  },
}
</script>
